import { ContractRequestDTO } from 'models/ContractRequestDTO'
import formatter from 'utils/formatter'
import { StepsFormProps } from '../interface'

const convertToTypeNewVehicleIndicator = (str: string): boolean | null => {
    if (['true', 'false'].includes(str)) {
        return str === 'true'
    }

    return null
}

export const mountContract = (data: StepsFormProps): ContractRequestDTO => {
    const {
        vehicles,
        vehiclesList,
        contract,
        complementary,
        financing,
        consortium,
        creditor,
        debtor,
        terceiroGarantidor,
        anexos
    } = data

    const percentuais = [
        'valorTaxaJurosAno',
        'valorTaxaJurosMes',
        'valorTaxaMora',
        'valorTaxaMulta'
    ]

    const indicador = [
        'identificadorTaxaMora',
        'identificadorTaxaMulta',
        'identificadorComissao',
        'indicacaoPenalidade'
    ]

    const currencies = [
        'valorIOF',
        'valorComissao',
        'valorParcela',
        'valorTaxaContrato',
        'valorTotalDivida',
        'fundoReserva'
    ]

    const hasValue = (value?: any) =>
        value !== undefined && value !== null && String(value).trim() !== ''

    const hasThirdGuarantor = (value?: any) => {
        if (!value) return false

        return (
            hasValue(value.nome) ||
            hasValue(value.email) ||
            hasValue(value.tipoDocumento) ||
            hasValue(value.documento) ||
            hasValue(value.telefone) ||
            hasValue(value.endereco?.cep) ||
            hasValue(value.endereco?.logradouro) ||
            hasValue(value.endereco?.numero) ||
            hasValue(value.endereco?.bairro) ||
            hasValue(value.endereco?.municipio) ||
            hasValue(value.endereco?.uf) ||
            hasValue(value.endereco?.complemento)
        )
    }

    const mountTerceiroGarantidor = (value?: any) => {
        if (!hasThirdGuarantor(value)) return undefined

        const municipio = value.endereco?.municipio || ''
        const [codigoMunicipio, nomeMunicipio] = municipio.includes('|')
            ? municipio.split('|')
            : ['', municipio]

        return {
            nome: value.nome,
            email: value.email,
            tipoDocumento: value.tipoDocumento,
            documento: formatter.cleanFormatter(value.documento),
            telefone: formatter.cleanFormatter(value.telefone),
            endereco: {
                ...value.endereco,
                cep: formatter.cleanFormatter(value.endereco?.cep),
                municipio: nomeMunicipio,
                codigoMunicipio: codigoMunicipio ? +codigoMunicipio : undefined
            }
        }
    }

    const allVehicles = [vehicles, ...((vehiclesList ?? []) as any[])].filter(Boolean)

    const veiculos = allVehicles.map(v => {
        const { ...rest } = v as any

        return {
            ...rest,
            identificacaoRemarcacao: Number((v as any).identificacaoRemarcacao ?? 0),
            ...(v.indicativoVeiculoNovo !== undefined && {
                indicativoVeiculoNovo: convertToTypeNewVehicleIndicator(
                    String(v.indicativoVeiculoNovo)
                )
            }),
            placa: (v as any).placa ? String((v as any).placa).replace(/\W/g, '') : ''
        }
    })

    const terceiroGarantidorPayload = mountTerceiroGarantidor(terceiroGarantidor)

    const { opcaoCompra, ...contractRest } = contract as any
    const opcaoCompraBoolean =
        typeof opcaoCompra === 'string'
            ? opcaoCompra.toUpperCase() === 'SIM'
            : opcaoCompra

    return {
        ...contractRest,

        ...(opcaoCompra !== undefined && opcaoCompra !== null
            ? { opcaoCompra: !!opcaoCompraBoolean }
            : {}),


        ufGravame: vehicles.ufGravame,

        veiculos,

        ...Object.assign(
            {},
            ...Object.entries(complementary).flatMap(([name, value]) => {
                if (currencies.includes(name)) {
                    return {
                        [name]: value ? +value.replace(/\D/g, '') / 100 : value
                    }
                } else if (percentuais.includes(name)) {
                    return {
                        [name]: value ? +value.replace(/\D/g, '') / 1000 : value
                    }
                } else if (name.includes('documento')) {
                    return {
                        [name]: formatter.cleanFormatter(value)
                    }
                } else if (indicador.includes(name)) {
                    let boolValue = false

                    if (value.toUpperCase() === 'SIM') {
                        boolValue = true
                    } else if (value.toUpperCase() === 'NAO') {
                        boolValue = false
                    }

                    return {
                        [name]: boolValue
                    }
                } else {
                    return { [name]: value }
                }
            })
        ),

        ...Object.assign(
            {},
            ...Object.entries(financing).flatMap(([name, value]) => {
                if (
                    [
                        'dataLiberacaoCredito',
                        'municipioLiberacaoCredito',
                        'ufLiberacaoCredito'
                    ].includes(name)
                ) {
                    return {
                        liberacaoCredito: {
                            dataLiberacaoCredito:
                                financing.dataLiberacaoCredito,
                            ufLiberacaoCredito: financing.ufLiberacaoCredito,
                            municipioLiberacaoCredito: {
                                codigo: parseInt(
                                    financing.municipioLiberacaoCredito.split('|')[0],
                                    10
                                ),
                                municipio:
                                    financing.municipioLiberacaoCredito.split('|')[1]
                            }
                        }
                    }
                } else if (currencies.includes(name)) {
                    return {
                        [name]: value ? +value.replace(/\D/g, '') / 100 : value
                    }
                } else {
                    return { [name]: value }
                }
            })
        ),

        ...consortium,
        ...anexos,

        origem: 'TELA',

        cotaConsorcio:
            consortium?.cotaConsorcio === '' ? null : consortium.cotaConsorcio,

        credor: {
            cnpj: formatter.cleanFormatter(creditor.cnpj),
            email: creditor.email,
            razaoSocial: creditor.razaoSocial,
            telefone: formatter.cleanFormatter(creditor.telefone),
            endereco: {
                ...creditor.endereco,
                cep: formatter.cleanFormatter(creditor.endereco.cep),
                municipio: creditor.endereco.municipio.split('|')[1],
                codigoMunicipio: +creditor.endereco.municipio.split('|')[0]
            }
        },

        devedor: {
            nome: debtor.nome,
            telefone: formatter.cleanFormatter(debtor.telefone),
            documento: formatter.cleanFormatter(debtor.documento),
            tipoDocumento: debtor.tipoDocumento,
            email: debtor.email,
            endereco: {
                ...debtor.endereco,
                cep: formatter.cleanFormatter(debtor.endereco.cep),
                municipio: debtor.endereco.municipio.split('|')[1],
                codigoMunicipio: +debtor.endereco.municipio.split('|')[0]
            }
        },

        ...(terceiroGarantidorPayload
            ? { terceiroGarantidor: terceiroGarantidorPayload }
            : {}),


        municipioContrato: {
            codigo: parseInt(contract.municipioContrato.split('|')[0], 10),
            municipio: contract.municipioContrato.split('|')[1]
        }
    }
}
